<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { Rocket, ExternalLink } from 'lucide-svelte';
  import { deploysApi } from '$lib/api/deploys';
  import { projectsApi } from '$lib/api/projects';
  import { getErrorMessage } from '$lib/api/client';
  import { relTime } from '$lib/utils/format';
  import { route } from '$lib/state/route.svelte';
  import EmptyState from '$lib/components/ui/EmptyState.svelte';
  import Chip from '$lib/components/ui/Chip.svelte';
  import DeployModal from './DeployModal.svelte';

  const deploysQuery = createQuery(() => ({
    queryKey: ['deploys'],
    queryFn: () => deploysApi.list()
  }));
  const projectsQuery = createQuery(() => ({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list()
  }));

  const sorted = $derived(
    [...(deploysQuery.data?.results ?? [])].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  );

  function projectName(id: number): string {
    return projectsQuery.data?.results.find((p) => p.id === id)?.name ?? '—';
  }
  function targetKind(target: string): 'lime' | 'warn' | 'gray' {
    if (/prod/i.test(target)) return 'lime';
    if (/beta/i.test(target)) return 'warn';
    return 'gray';
  }

  let showModal = $state(false);
</script>

<div class="page-head">
  <div>
    <h1 class="h-title">Deploys</h1>
    <div class="h-sub">Every deploy across all projects, newest first</div>
  </div>
  <span class="spacer"></span>
  <button type="button" class="btn btn-primary" onclick={() => (showModal = true)}>
    <Rocket size={16} /> Record deploy
  </button>
</div>

{#if deploysQuery.isLoading}
  <p style="color:var(--t3);font-size:13.5px">Loading deploys…</p>
{:else if deploysQuery.isError}
  <p style="color:var(--danger);font-size:13.5px">{getErrorMessage(deploysQuery.error)}</p>
{:else if sorted.length}
  <div class="card table-scroll">
    <table class="table">
      <thead>
        <tr>
          {#each ['Project', 'Commit', 'Target', 'When', 'Triggered by', 'Artifact'] as h (h)}
            <th>{h}</th>
          {/each}
        </tr>
      </thead>
      <tbody>
        {#each sorted as dep (dep.id)}
          <tr class="clickable" onclick={() => route.push({ tab: 'project', projectId: dep.project_id })}>
            <td><span style="color:var(--t1);font-weight:600">{projectName(dep.project_id)}</span></td>
            <td><span class="mono">{dep.sha}</span></td>
            <td><Chip label={dep.target} kind={targetKind(dep.target)} /></td>
            <td>{relTime(dep.created_at)}</td>
            <td>{dep.by}</td>
            <td onclick={(e) => e.stopPropagation()}>
              {#if dep.artifact}
                <a class="linkchip" style="padding:4px 9px" href="https://{dep.artifact}" target="_blank" rel="noreferrer">
                  <ExternalLink size={14} />Build
                </a>
              {:else}
                <span style="color:var(--t3)">—</span>
              {/if}
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{:else}
  <EmptyState icon={Rocket} heading="No deploys yet" sub="Deploys recorded from CI or manually will show up here, newest first." />
{/if}

{#if showModal}
  <DeployModal projects={projectsQuery.data?.results ?? []} onClose={() => (showModal = false)} />
{/if}
